// src/components/app-shell/AppShellHandlers.ts
import { firstValueFrom, of, throwError } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { apiService } from '../../services/api-service'
import { chatStore } from '../../store'
import { AppShell } from './AppShell'

export class AppShellHandlers {
    constructor(private appShell: AppShell) {
        // Bind methods to preserve 'this' context
        this.handleChatSelected = this.handleChatSelected.bind(this)
        this.handleMessageSubmit = this.handleMessageSubmit.bind(this)
        this.handleNewChatRequested = this.handleNewChatRequested.bind(this)
        this.toggleChatList = this.toggleChatList.bind(this)
    }

    handleChatSelected(e: CustomEvent) {
        const chatId = (e.detail as { id: string })?.id
        if (chatId) {
            this.setActiveConversation(chatId)
        }
    }

    handleMessageSubmit(e: CustomEvent<{ message: string }>) {
        const chatId = chatStore.getActiveChat()?.id
        if (!chatId) return

        const activeCharacter = chatStore.getState().activeCharacter

        // Add user message to UI immediately
        chatStore.addUserMessage(e.detail.message, chatId)

        if (activeCharacter) {
            void this.sendCharacterMessage(activeCharacter, e.detail.message, chatId)
        } else {
            void this.sendRegularMessage(chatId, e.detail.message)
        }
    }

    private async sendCharacterMessage(characterId: string, message: string, chatId: string) {
        try {
            const response = await firstValueFrom(
                apiService.sendCharacterMessage(characterId, message),
            )
            if (response) {
                chatStore.addBotMessage(response.message, chatId)
                if (response.state) {
                    chatStore.updateCharacterState(chatId, response.state)
                }
            }
        } catch (error) {
            console.error('Failed to send character message:', error)
            chatStore.setError('Failed to send message. Please try again.')
        }
    }

    private async sendRegularMessage(chatId: string, message: string) {
        try {
            const response = await firstValueFrom(apiService.sendMessage(chatId, message))
            chatStore.addBotMessage(response.content, chatId)
        } catch (error) {
            console.error('Failed to send message:', error)
            chatStore.setError('Failed to send message. Please try again.')
        }
    }

    handleNewChatRequested() {
        const currentState = chatStore.getState()
        if (currentState.isLoading) return // Prevent multiple simultaneous requests

        void firstValueFrom(
            apiService.getPersonalities().pipe(
                switchMap(personalities => {
                    if (!personalities || personalities.length === 0) {
                        return throwError(() => new Error('No personalities available'))
                    }

                    const randomIndex = Math.floor(Math.random() * personalities.length)
                    const randomPersonality = personalities[randomIndex]

                    return apiService.createConversation(randomPersonality.id)
                }),
                tap(newConversation => {
                    chatStore.initializeChat(
                        newConversation.id,
                        newConversation.philosopherName,
                        '',
                        newConversation.philosopherId,
                    )
                    this.setActiveConversation(newConversation.id)
                    if (this.appShell._isMobile) {
                        this.appShell._showChatList = false
                    }
                }),
                catchError(error => {
                    console.error('Failed to create new conversation:', error)
                    chatStore.setError('Failed to create new conversation. Please try again.')
                    return of(void 0)
                }),
            ),
        )
    }

    toggleChatList() {
        this.appShell._showChatList = !this.appShell._showChatList
    }

    private async setActiveConversation(conversationId: string) {
        const isCharacterChat = chatStore.getState().characters[conversationId] !== undefined

        chatStore.setActiveChat(conversationId)

        if (isCharacterChat) {
            await this.handleCharacterChat(conversationId)
        } else {
            await this.handleRegularChat(conversationId)
        }
    }

    private async handleCharacterChat(conversationId: string) {
        try {
            const messages = await firstValueFrom(apiService.getCharacterHistory(conversationId))
            chatStore.clearMessages(conversationId)

            if (messages.length === 0) {
                await this.initializeCharacterChat(conversationId)
            } else {
                this.addMessagesToStore(messages, conversationId)
            }
        } catch (error) {
            console.error('Failed to load character history:', error)
            const currentChat = chatStore.getState().chats[conversationId]
            if (!currentChat || currentChat.messages.length === 0) {
                await this.initializeCharacterChat(conversationId)
            }
        }
    }

    private async handleRegularChat(conversationId: string) {
        try {
            const messages = await firstValueFrom(apiService.getMessages(conversationId))
            chatStore.clearMessages(conversationId)
            this.addMessagesToStore(messages, conversationId)
        } catch (error) {
            console.error('Failed to load messages:', error)
        }
    }

    private async initializeCharacterChat(conversationId: string) {
        try {
            const response = await firstValueFrom(
                apiService.sendCharacterMessage(conversationId, '__init__'),
            )
            if (response) {
                chatStore.addBotMessage(response.message, conversationId)
                if (response.state) {
                    chatStore.updateCharacterState(conversationId, response.state)
                }
            }
        } catch (error) {
            console.error('Failed to initialize character chat:', error)
        }
    }

    private addMessagesToStore(messages: any[], conversationId: string) {
        messages.forEach(msg => {
            if (msg.role === 'user') {
                chatStore.addUserMessage(msg.content, conversationId, true)
            } else {
                chatStore.addBotMessage(msg.content, conversationId, true)
            }
        })
    }
}
